import { Controller } from "@hotwired/stimulus";
import { Canvas, FabricImage, FabricObject, Rect, Group, Path, Circle } from "fabric";
import { FetchRequest } from "@rails/request.js";
import {
  switchClip,
  resizeCanvas,
  computeDimensions,
  compactAroundGroup,
  objectAvaibleControls,
  CustomTextbox as IText,
  toggleOutline,
  createRect,
  createImage,
  switchShape

} from "../utils/fabric";
import { emitEditRendering, emitEditSaved } from "../utils/events";

export default class extends Controller {
  static targets = [
    "canvas",
    "textButton",
    "imageButton",
    "shapeButton",
    "deleteButton",
    "outlineButton",
    "clipButton",
    "saveButton",
    "colorPicker",
    "layersData",
    "canvasData",
    "transformedMedium",
    "container",
    "layerControls",
    "fontButton",
  ];

  static values = {
    width: Number,
    height: Number,
    imageUrl: String,
    editId: Number,
    layers: Array,
    ajaxCall: Boolean,
    standAlone: Boolean,
    fonts: Object,
    origin: String,
    imagePickerOrigin: String,
  };

  // si pas d'image => canvas 300x300
  // sinon => la plus longue dimension réduite à 1000, l'autre proportionnelle

  async connect() {
    // on prépare le réferentiel
    const [width, height, ratio] = computeDimensions(
      this.widthValue,
      this.heightValue
    );
    this.canvasRatio = ratio;
    this.canvas = new Canvas(this.canvasTarget, {
      //backgroundColor: "#f8f9fa",
      width,
      height,
      preserveObjectStacking: true,
    });

    await resizeCanvas(this.canvas, this.containerTarget);
    this.fontsValue && this.loadFonts();
    await this.loadBackgroundImage(ratio);
    this.layersValue && (await this.loadLayers(this.layersValue));

    // On active les controls de base
    if (this.canvas.getActiveObjects().length > 0) {
      this.handleSelection();
    } else {
      this.selectedObject = null;
    }

    this.canvas.renderAll();
    this.setupEventListeners();

    this.canvas.on("selection:created", this.handleSelection.bind(this));
    this.canvas.on("selection:updated", this.handleSelection.bind(this));
    this.canvas.on("selection:cleared", this.handleDeselection.bind(this));
    this.canvas.on("object:modified", this.updateLayersData.bind(this));

    // Bon là il s'agit d'avoir un this.addMedia avec un bind intégré sans avoir à déclarer
    // le bind.this à la volée ce qui crée une nouvelle fonction et donc empêche le removeEventListener...
    this.boundAddMedia = this.addMedia.bind(this);
    document.addEventListener("content_medium:selected", this.boundAddMedia);

    this.placeButtonBar() // le plus tard possible pour les bonnes dimensions
  }

  disconnect() {
    document.removeEventListener("content_medium:selected", this.boundAddMedia);
    this.canvas.dispose();
  }

  placeButtonBar() {
    const top = this.containerTarget.getBoundingClientRect()?.top
    if (window.innerWidth < 736 && top > 80) {
      this.layerControlsTarget.style.top = "-52px";
    }
  }

  async loadFonts() {
    Promise.all(
      Object.entries(this.fontsValue).map(([name, url]) => {
        console.log(name, url);
        return new FontFace(name, url, {
          style: "normal",
          weight: "normal",
        }).load();
      })
    ).then((fontFaces) => {
      fontFaces.forEach((f) => document.fonts.add(f));
    });
  }

  async loadBackgroundImage(ratio) {
    const img = await FabricImage.fromURL(this.imageUrlValue, {
      crossOrigin: "anonymous",
    });
    img.set({
      originX: "center",
      originY: "center",
      scaleX: ratio,
      scaleY: ratio,
      left: this.canvas.width / 2,
      top: this.canvas.height / 2,
      selectable: false, // Background can't be selected
      layerId: "originalImage",
    });
    this.canvas.add(img);
  }

  async loadLayers(layers) {
    const promises = layers.map(async (layer) => {
      return await this.loadLayer(layer);
    });
    const objects = await Promise.all(promises).then((allObjects) => {
      return allObjects.map((obj) => {
        return this.createObject(obj);
      });
    });
    return objects;
  }

  loadLayer = async (layer) => {
    switch (layer.type) {
      case "IText":
        const text = await IText.fromObject(layer);
        text.hiddenTextareaContainer = this.containerTarget; // to prevent modal from steeling focus
        text.charSpacing = 1;
        return text;
      case "Image":
        const img = await FabricImage.fromObject(layer);
        return img;
      case "Group":
        return await Group.fromObject(layer);
      case "Rect":
        return await Rect.fromObject(layer);
      case "Path":
        return await Path.fromObject(layer);
      case "Circle":
        return await Circle.fromObject(layer);
      case "shape":
        break;
    }
  };

  up() {
    this.canvas.bringObjectForward(this.selectedObject, true);
    this.canvas.renderAll();
  }

  down() {
    if (this.canvas._objects.indexOf(this.selectedObject) <= 1) return;
    this.canvas.sendObjectBackwards(this.selectedObject);
    this.canvas.renderAll();
  }

  addMedia(e) {
    const {
      contentMedium: {
        url,
        edit,
      },
      origin,
    } = e.detail;
    const finalUrl = edit?.transformedUrl || url;
    if (origin === this.imagePickerOriginValue) {
      this.addImageLayer(finalUrl);
    }
  }

  groupObjets(objects) {
    const group = new Group(objects);

    // On dégage tous les objets pour ne laisser que le group et ses objets
    objects.forEach((obj) => {
      this.canvas.remove(obj);
    });

    this.canvas.add(group);
    this.canvas.setActiveObject(group);
    this.canvas.requestRenderAll();

    this.selectedObject = group;
  }

  createObject(obj) {
    console.log("obj", obj)
    this.canvas.add(obj);
    //addCtrl(obj, this.canvas);
    this.canvas.setActiveObject(obj);
    return obj;
  }

  setupEventListeners() {
    this.textButtonTarget.addEventListener("click", () => {
      this.addTextLayer.bind(this)();
    });
    this.shapeButtonTarget.addEventListener("click", () => {
      this.addShapeLayer.bind(this)();
    });
    this.deleteButtonTarget.addEventListener(
      "click",
      this.deleteSelectedLayer.bind(this)
    );

    this.outlineButtonTarget.addEventListener("click", this.toggleOutline.bind(this));
    this.clipButtonTarget.addEventListener("click", this.switchShape.bind(this));
    this.saveButtonTarget.addEventListener(
      "click",
      this.saveChanges.bind(this)
    );
    this.colorPickerTarget.addEventListener(
      "change",
      this.changeColor.bind(this)
    );
  }

  async rasterize() {
    if (this.hasTransformedMediumTarget) {
      // Ici on recouvre l'image pendant qu'on re-zoom pour éviter que ça soit cheum
      const img = this.containerTarget.children[0];
      img.src = this.canvas.toDataURL();
      img.width = this.canvas.width;
      img.height = this.canvas.height;
      img.style.transformOrigin = "top left";
      img.style.transform = `scale(${1 / this.canvas.getZoom()})`;
      // TODO: à terminer
      await new Promise((r) =>
        setTimeout(() => {
          console.log("oi");
          r();
        }, 100)
      );
      console.log("ok c bon");
      this.canvas.setZoom(1);
      this.transformedMediumTarget.value = this.canvas.toDataURL();
    }
  }

  addTextLayer(text = "Double-click to edit", options = {}) {
    const textObj = new IText(text, {
      ...this.newLayerDefaults,
      fontFamily: "Arial",
      fontSize: 24,
      fill: "#000000",
      hiddenTextareaContainer: this.containerTarget,
      ...options,
    });
    this.createObject(textObj);
  }

  async addImageLayer(imageUrl, options = {}) {
    console.log(imageUrl);
    if (imageUrl) {
      const img = await createImage(imageUrl, { left: 100, right: 100, ...options })
      this.createObject(img);
    }
  }

  async addShapeLayer(options = {}) {
    const rect = createRect({

      ...this.newLayerDefaults,
      layerType: "shape",
      fill: "#ffffff",
      width: 300,
      height: 300,
      ...options,
    })
    this.createObject(rect);
  }

  switchShape = () => {
    const obj = this.selectedObject;
    if (obj instanceof FabricImage) {
      switchClip(obj);
      obj.dirty = true;
      this.canvas.remove(obj);
      this.createObject(obj);
    } else if (obj instanceof FabricObject) {
      const nextObj = switchShape(obj);
      this.createObject(nextObj);
      this.canvas.remove(obj);
    }
  }

  toggleOutline = () => {
    const obj = this.selectedObject;
    toggleOutline(obj);
    this.canvas.renderAll()
  }

  get newLayerDefaults() {
    return {
      left: 100,
      top: 100,
      layerId: this.generateLayerId(),
    };
  }

  async mergeLayers(e) {
    const layers = JSON.parse(e.currentTarget.dataset.layers);
    const objects = await this.loadLayers(layers);
    // On ajoute les calques en tant que GROUP
    this.groupObjets(objects);
  }

  changeFont(e) {
    const font = e.currentTarget.dataset.font;
    const obj = this.selectedObject;
    if (obj && obj instanceof IText) {
      obj.set({ fontFamily: font });
    }
    this.canvas.requestRenderAll();
  }

  // Helper methods
  generateLayerId() {
    return "layer_" + Date.now() + "_" + Math.floor(Math.random() * 1000);
  }

  deactivateLayerControls() {
    this.clipButtonTarget.classList.add("hidden");
    this.colorPickerTarget.classList.add("hidden");
    this.fontButtonTarget.classList.add("hidden");
    this.outlineButtonTarget.classList.add("hidden");
  }

  activateLayerControls(object) {
    const objectType = objectAvaibleControls.find(
      (obj) => obj.type == object.type
    );
    objectType?.options?.forEach((option) => {
      switch (option) {
        case "clip":
          return this.clipButtonTarget.classList.remove("hidden");
        case "color":
          return this.colorPickerTarget.classList.remove("hidden");
        case "font":
          return this.fontButtonTarget.classList.remove("hidden");
        case "outline":
          return this.outlineButtonTarget.classList.remove("hidden");
      }
    });
  }

  handleSelection(e) {
    this.selectedObject = this.canvas.getActiveObject();

    this.deactivateLayerControls();
    this.activateLayerControls(this.selectedObject);
    this.layerControlsTarget.classList.remove("hidden");
    this.deleteButtonTarget.disabled = false;

    // On peut sélectionner plusieurs éléments
    // et en faire un group
    if (this.selectedObject.type === "activeselection") {
      this.selectedObject = e.selected;
      return;
    }

    if (this.selectedObject.get("fill")) {
      this.colorPickerTarget.value = this.rgbToHex(
        this.selectedObject.get("fill")
      );
    }
  }

  handleDeselection() {
    this.layerControlsTarget.classList.add("hidden");
    this.selectedObject = null;
    this.deleteButtonTarget.disabled = true;
  }

  deleteSelectedLayer() {
    if (!this.selectedObject) return;

    // En cas de sélection de plusieurs objets
    if (this.selectedObject?.length) {
      this.selectedObject.forEach((obj) => {
        this.canvas.remove(obj);
      });
      this.canvas.discardActiveObject();
    }

    this.canvas.remove(this.selectedObject);
    this.selectedObject = null;
    this.deleteButtonTarget.disabled = true;
    this.updateLayersData();
  }

  changeColor(e) {
    if (!this.selectedObject) return;

    const color = e.target.value;

    if (this.selectedObject.get("type") === "i-text") {
      this.selectedObject.set("fill", color);
    } else {
      const property = this.selectedObject.stroke ? "stroke" : "fill"
      this.selectedObject.set(property, color);
    }

    this.canvas.renderAll();
    this.updateLayersData();
  }

  updateLayersData() {
    this.layers.map((obj) => obj.toJSON());

    // Update hidden field with JSON data
    if (this.hasLayersDataTarget) {
      this.layersDataTarget.value = JSON.stringify(this.layers);
    }
  }

  get layers() {
    return this.canvas
      .getObjects()
      .filter((obj) => obj.get("layerId") !== "originalImage");
  }

  async saveChanges(e) {
    // On déselectionne pour éviter les pbs de rendu
    this.canvas.discardActiveObject();
    this.saveButtonTarget.disabled = true;
    // À rechecker
    this.standAloneValue &&
      compactAroundGroup(this.canvas, new Group(this.layers));

    await this.rasterize();
    this.updateLayersData();
    window.closeDialog && window.closeDialog(this.originValue);

    emitEditRendering(this.imageUrlValue)

    const form = this.layersDataTarget.form;
    if (this.ajaxCallValue) {
      // for testing purposes
      const json = await this.ajaxSave(form);
      if (!json?.data) {
        return;
      }
      const {
        id,
        transformed_medium: { url: transformedUrl },
      } = json.data;
      emitEditSaved(id, transformedUrl, this.imageUrlValue)
    } else {
      form.requestSubmit();
    }
  }

  async ajaxSave(form) {
    const body = new FormData(form);
    const request = new FetchRequest(form.method, form.action, {
      body,
      headers: { Accept: "application/json" },
    });
    const response = await request.perform();
    if (response.ok) {
      return await response.json;
    } else {
      console.log("Aïe");
    }
  }

  // Utility function to convert RGB to Hex
  rgbToHex(rgb) {
    if (!rgb) return "#000000";
    if (rgb.startsWith("#")) return rgb;

    // Parse rgb(r, g, b) format
    const rgbMatch = rgb.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1], 10);
      const g = parseInt(rgbMatch[2], 10);
      const b = parseInt(rgbMatch[3], 10);

      return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    }

    return "#000000";
  }
}
